import { Keyboard } from "lucide-react";
import type { JSX } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mailflare/ui";

export interface ShortcutDefinition {
  readonly keys: string;
  readonly description: string;
}

/** Single source of truth for the shortcuts overlay and the README. */
export const SHORTCUTS: readonly ShortcutDefinition[] = [
  { keys: "?", description: "Show or hide this help" },
  { keys: "c", description: "Compose a new message" },
  { keys: "/", description: "Focus the search box" },
  { keys: "j", description: "Next message" },
  { keys: "k", description: "Previous message" },
  { keys: "e", description: "Archive the focused message" },
  { keys: "#", description: "Move the focused message to trash" },
  { keys: "r", description: "Reply to the focused message" },
  { keys: "s", description: "Toggle the star on the focused message" },
  { keys: "Esc", description: "Close dialogs and clear focus" },
];

export interface ShortcutsHelpDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function ShortcutsHelpDialog({ open, onOpenChange }: ShortcutsHelpDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Navigate MailFlare without leaving the keyboard.</DialogDescription>
        </DialogHeader>
        <Table>
          <TableBody>
            {SHORTCUTS.map((shortcut) => (
              <TableRow key={shortcut.keys}>
                <TableCell className="text-muted-foreground">{shortcut.description}</TableCell>
                <TableCell className="text-right">
                  <kbd className="bg-muted text-foreground rounded border px-1.5 py-0.5 font-mono text-xs">
                    {shortcut.keys}
                  </kbd>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Keyboard aria-hidden className="size-3.5" />
          Shortcuts are ignored while typing in a field.
        </p>
      </DialogContent>
    </Dialog>
  );
}
